/* src/components/customGame/setup/panels/StimulusPanelController.js */
import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../../ui/Accordion';

// Import individual stimulus panels and resolve them immediately
import ImportedAudioStimulusPanel from './stimuli/AudioStimulusPanel';
const AudioStimulusPanel = ImportedAudioStimulusPanel.default || ImportedAudioStimulusPanel;

import ImportedPositionStimulusPanel from './stimuli/PositionStimulusPanel';
const PositionStimulusPanel = ImportedPositionStimulusPanel.default || ImportedPositionStimulusPanel;

import ImportedColorStimulusPanel from './stimuli/ColorStimulusPanel';
const ColorStimulusPanel = ImportedColorStimulusPanel.default || ImportedColorStimulusPanel;

import ImportedShapeStimulusPanel from './stimuli/ShapeStimulusPanel';
const ShapeStimulusPanel = ImportedShapeStimulusPanel.default || ImportedShapeStimulusPanel;

import ImportedSymbolicStimulusPanel from './stimuli/SymbolicStimulusPanel';
const SymbolicStimulusPanel = ImportedSymbolicStimulusPanel.default || ImportedSymbolicStimulusPanel;


// --- DEBUGGING LOGS for resolved components ---
console.log('[StimulusPanelController] AudioStimulusPanel (resolved):', typeof AudioStimulusPanel, AudioStimulusPanel);
console.log('[StimulusPanelController] PositionStimulusPanel (resolved):', typeof PositionStimulusPanel, PositionStimulusPanel);
console.log('[StimulusPanelController] ColorStimulusPanel (resolved):', typeof ColorStimulusPanel, ColorStimulusPanel);
console.log('[StimulusPanelController] ShapeStimulusPanel (resolved):', typeof ShapeStimulusPanel, ShapeStimulusPanel);
console.log('[StimulusPanelController] SymbolicStimulusPanel (resolved):', typeof SymbolicStimulusPanel, SymbolicStimulusPanel);
// --- END DEBUGGING LOGS ---


const ALL_STIMULUS_TYPES = [
    {
        key: 'Audio',
        label: 'Auditory Stimuli',
        panelComponent: AudioStimulusPanel, // Use the resolved component
        defaultProps: { SetType: "Letters", LetterSet: "Standard Consonants (Jaeggi)", WordList: "Theme: Objects", SimilarityMode: "None (Distinct Items)", SimilarityLevel: 0.5, Source: "Pre-recorded Pack", TTSVoice: "System Default", Spatialization: 0.0, Volume: 1.0 }
    },
    {
        key: 'Position',
        label: 'Position Stimuli (Visual-Spatial)',
        panelComponent: PositionStimulusPanel, // Use the resolved component
        defaultProps: { GridSize: "3x3", GridWidth: 3, GridHeight: 3, AllowedLocations: [], StimulusShape: "Square", StimulusColor: "#3B82F6", GridAppearance: "Visible Borders", BackgroundColor: "#F3F4F6" }
    },
    {
        key: 'Color',
        label: 'Color Stimuli (Visual)',
        panelComponent: ColorStimulusPanel, // Use the resolved component
        defaultProps: { StimulusShape: "Central Square", PaletteType: "Predefined Set (Basic 8)", PaletteColors: ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#000000", "#FFFFFF"], PaletteColorsUserString:"#FF0000,#00FF00,#0000FF,#FFFF00,#FF00FF,#00FFFF,#000000,#FFFFFF", NumberOfColors: 8, SimilarityMetric: "HSL Distance", MinSimilarityDistance: 0.3 }
    },
    {
        key: 'Shape',
        label: 'Shape Stimuli (Visual)',
        panelComponent: ShapeStimulusPanel, // Use the resolved component
        defaultProps: { SetType: "Geometric (Simple)", ShapeList:["Square", "Circle", "Triangle"], ShapeListUserString:"Square,Circle,Triangle", ComplexityControl: 0.5, RotationInvariance: true, SizeVariation: "Fixed Size", FillStyle: "Filled", LineThickness: 2, Color: "#4F46E5" }
    },
    {
        key: 'Symbolic',
        label: 'Symbolic/Abstract Stimuli (Visual)',
        panelComponent: SymbolicStimulusPanel, // Use the resolved component
        defaultProps: { SetType: "Phonologically Distinct Letters (Research)", SymbolList: "B,K,Q,T,H,M,N,P,X,R" }
    },
];


const SettingsSection = ({ title, children, className = "" }) => (
    <div className={`mb-6 p-4 border border-gray-200 rounded-md shadow-sm bg-white ${className}`}>
        <h3 className="text-lg font-medium text-gray-800 mb-3">{title}</h3>
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

SettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};
SettingsSection.defaultProps = {
    children: null,
    className: '',
};


const StimulusPanelController = ({ config, onConfigChange, pathPrefix }) => {
    const enabledTypes = config?.EnabledTypes || [];

    const handleEnabledTypeChange = (stimulusKey, isChecked) => {
        let newEnabledTypes;
        if (isChecked) {
            newEnabledTypes = [...enabledTypes, stimulusKey];
        } else {
            newEnabledTypes = enabledTypes.filter(type => type !== stimulusKey);
        }
        onConfigChange(`${pathPrefix}.EnabledTypes`, newEnabledTypes);

        if (isChecked && (!config[stimulusKey] || Object.keys(config[stimulusKey]).length === 0) ) {
            const stimulusDefinition = ALL_STIMULUS_TYPES.find(st => st.key === stimulusKey);
            if (stimulusDefinition && stimulusDefinition.defaultProps) {
                onConfigChange(`${pathPrefix}.${stimulusKey}`, stimulusDefinition.defaultProps);
            } else {
                 onConfigChange(`${pathPrefix}.${stimulusKey}`, {});
            }
        }
    };

    console.log('[StimulusPanelController Render] config.EnabledTypes:', enabledTypes);

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">
            <SettingsSection title="Select Active Stimulus Modalities">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ALL_STIMULUS_TYPES.map(stimulus => (
                        (stimulus.panelComponent) && // panelComponent should now always be the correct function or undefined
                        <div key={stimulus.key} className="flex items-center p-3 border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors">
                            <input
                                id={`stimulus-type-${stimulus.key}`}
                                name={stimulus.key}
                                type="checkbox"
                                className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                checked={enabledTypes.includes(stimulus.key)}
                                onChange={(e) => handleEnabledTypeChange(stimulus.key, e.target.checked)}
                            />
                            <label htmlFor={`stimulus-type-${stimulus.key}`} className="ml-3 block text-sm font-medium text-gray-700 select-none">
                                {stimulus.label}
                            </label>
                        </div>
                    ))}
                </div>
                <p className="mt-3 text-xs text-gray-500">
                    Select one or more stimulus types to include in your custom game. Detailed settings for each enabled type will appear below.
                </p>
            </SettingsSection>

            {/* Render panels for enabled types */}
            {enabledTypes.map(enabledKey => {
                const stimulus = ALL_STIMULUS_TYPES.find(st => st.key === enabledKey);
                if (!stimulus || !stimulus.panelComponent) {
                    console.warn(`[StimulusPanelController Map] No panelComponent found for enabled key: ${enabledKey}`);
                    return (
                        <div key={`${enabledKey}-error-missing`} className="text-red-500 p-4 bg-red-50 border border-red-200 rounded-md">
                            Error: Configuration panel for {enabledKey} is missing.
                        </div>
                    );
                }
                
                const PanelComponent = stimulus.panelComponent; // This should be the resolved function

                // This check should ideally not fail if the import resolution logic above is correct
                if (typeof PanelComponent !== 'function') {
                    console.error(`[StimulusPanelController Error] PanelComponent for ${stimulus.key} is NOT a function. Got:`, typeof PanelComponent, PanelComponent);
                    return (
                        <div key={`${stimulus.key}-error-invalid`} className="text-red-500 p-4 bg-red-50 border border-red-200 rounded-md">
                            Error: Could not load settings panel for {stimulus.label}. Component is invalid (Type: {typeof PanelComponent}).
                        </div>
                    );
                }

                return (
                    <Accordion
                        key={`${stimulus.key}-config-accordion`}
                        title={`${stimulus.label} Settings`}
                        id={`${stimulus.key}-accordion`}
                        defaultOpen={enabledTypes.length === 1} // Open if it's the only one, or adjust as needed
                        className="mt-0 mb-6"
                    >
                        <div className="p-4 bg-white border-t border-gray-200 rounded-b-md">
                            <PanelComponent
                                config={config?.[stimulus.key] || stimulus.defaultProps || {}}
                                onConfigChange={onConfigChange}
                                pathPrefix={`${pathPrefix}.${stimulus.key}`}
                                globalStimulusConfig={config}
                            />
                        </div>
                    </Accordion>
                );
            })}
            
            {enabledTypes.length === 0 && (
                 <div className="p-6 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-md">
                    <p className="font-medium">No stimulus modalities selected.</p>
                    <p className="text-sm">Please enable at least one stimulus type to configure its settings.</p>
                </div>
            )}
        </div>
    );
};

StimulusPanelController.propTypes = {
    config: PropTypes.shape({
        EnabledTypes: PropTypes.arrayOf(PropTypes.string),
        // Each stimulus type will also be a key, e.g., Audio: PropTypes.object
    }),
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired,
};

StimulusPanelController.defaultProps = {
    config: { 
        EnabledTypes: [],
        Audio: ALL_STIMULUS_TYPES.find(s => s.key === 'Audio')?.defaultProps || {}, 
        Position: ALL_STIMULUS_TYPES.find(s => s.key === 'Position')?.defaultProps || {},
        Color: ALL_STIMULUS_TYPES.find(s => s.key === 'Color')?.defaultProps || {},
        Shape: ALL_STIMULUS_TYPES.find(s => s.key === 'Shape')?.defaultProps || {},
        Symbolic: ALL_STIMULUS_TYPES.find(s => s.key === 'Symbolic')?.defaultProps || {},
    }
};

export default StimulusPanelController;